import { CheckCircle2, History, LayoutDashboard, Plus, Settings2 } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import styles from './ExpensesNavigator.module.css'

// Screens that own a single record (Add / Review one item / Edit one history
// entry) already ship their own header + back link + sticky action bar, so
// on mobile they get no chrome from here at all — one bottom bar per screen,
// never two stacked. List/dashboard screens get the bottom tab bar instead.
const isTaskRoute = (pathname) => (
    pathname === '/expenses/add'
    || (pathname.startsWith('/expenses/review/') && pathname !== '/expenses/review/')
    || (pathname.startsWith('/expenses/history/') && pathname !== '/expenses/history/')
)

const TABS = [
    { to: '/expenses/summary', label: 'Summary', Icon: LayoutDashboard },
    { to: '/expenses/history', label: 'All Expenses', Icon: History },
    { to: '/expenses/add', label: 'Add', Icon: Plus, isFab: true },
    { to: '/expenses/review', label: 'Review', Icon: CheckCircle2 },
    { to: '/settings/expenses', label: 'Settings', Icon: Settings2 },
]

const ExpensesNavigator = () => {
    const location = useLocation()

    const isActive = (path, { startsWith = false } = {}) => (
        startsWith ? location.pathname.startsWith(path) : location.pathname === path
    )

    const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false))

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768)
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    // Every /expenses/* screen occupies the bottom of the viewport on mobile —
    // either with the tab bar below, or with the page's own sticky action bar
    // on task screens. Either way the floating hamburger FAB must park
    // top-left instead of bottom-right, where it'd sit on top of both.
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('layout:fixedBottomBar', { detail: { active: true } }))
        return () => {
            window.dispatchEvent(new CustomEvent('layout:fixedBottomBar', { detail: { active: false } }))
        }
    }, [])

    const showMobileTabBar = isMobile && !isTaskRoute(location.pathname)

    return (
        <>
            <div className={styles.headerWrapper}>
                <div className={styles.allTitles}>
                    <Link to="/expenses/summary" className={isActive('/expenses/summary') ? styles.eachActiveTitle : styles.eachTitle}>
                        Summary
                    </Link>
                    <Link to="/expenses/history" className={isActive('/expenses/history', { startsWith: true }) ? styles.eachActiveTitle : styles.eachTitle}>
                        All Expenses
                    </Link>
                    <Link to="/expenses/add" className={isActive('/expenses/add') ? styles.eachActiveTitle : styles.eachTitle}>
                        Add New
                    </Link>
                    <Link to="/expenses/review" className={isActive('/expenses/review', { startsWith: true }) ? styles.eachActiveTitle : styles.eachTitle}>
                        Review
                    </Link>
                    <Link to="/settings/expenses" className={isActive('/settings/expenses') ? styles.eachActiveTitle : styles.eachTitle}>
                        Settings
                    </Link>
                </div>
            </div>
            <div className={styles.headerSpacer} />

            {showMobileTabBar && (
                <nav className={styles.mobileTabBar}>
                    {TABS.map(({ to, label, Icon, isFab }) => {
                        const active = to === '/expenses/history'
                            ? isActive(to, { startsWith: true })
                            : to === '/expenses/review'
                                ? isActive(to, { startsWith: true })
                                : isActive(to)

                        if (isFab) {
                            return (
                                <Link key={to} to={to} className={styles.mobileFabWrap} aria-label={label}>
                                    <span className={styles.mobileFab}>
                                        <Icon size={24} strokeWidth={2.5} />
                                    </span>
                                </Link>
                            )
                        }

                        return (
                            <Link
                                key={to}
                                to={to}
                                className={active ? styles.mobileTabActive : styles.mobileTab}
                            >
                                <Icon size={20} strokeWidth={active ? 2.25 : 1.9} />
                                <span>{label}</span>
                            </Link>
                        )
                    })}
                </nav>
            )}
        </>
    )
}

export default ExpensesNavigator
